import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import debounce from "lodash.debounce";
import { fetchClaimFact, fetchClaimsByAccountKey, fetchClaimDetails, fetchClaimDetailsByHccID } from "../../utils/apiUtils";
import { loadAccounts } from "../../utils/optionLoaderUtils";
import { customStyles } from "../../utils/styleUtils";
import SharedTable from "../benefitPlan/SharedTable";
import Select from 'react-select';
import { AsyncPaginate } from "react-select-async-paginate";
import { Link } from "react-router";
import { camelCaseToWords } from "../../utils/commonUtils";

const limit = 20;
const claimStatusOptions = ['Final',
    'Needs Repair',
    'Needs Review',
    'Rejected',
    'Denied',
    'Needs Repricing'].map(str => ({ value: str, label: str }))

const searchOptions = [
    'memberKey',
    'claimHccId',
    'externalClaimNumber',
    'accountKey',
].map(str => ({ value: str, label: camelCaseToWords(str) }))

const claimDetailFlags = {
    includeAccounts: true,
    includeAccumulators: true,
    includeBenefitNetwork: true,
    includeBenefitPlan: true,
    includeExternalRepricerResult: true,
    includeFundedAccounts: true,
    includeIngenixExternalLineResult: true,
    includeReportingCategoriesDescription: true,
    includeRepricerName: true,
    includePractitioner: true,
    includeProcedureInformation: true,
    includeProduct: true,
    includeReportingCategories: true,
    includeSupplier: true,
    includeSupplierInvoice: true,
    includeSupplierLocation: true,
}

const Claims = ({ memberKey }) => {

    const [searchValue, setSearchValue] = useState();
    const [decouncedSearchValue, setDecouncedSearchValue] = useState();
    const [selectedAccount, setSelectedAccount] = useState(null);

    const [claimStatus, setClaimStatus] = useState(null);
    const [searchBy, setSearchBy] = useState(null);
    const accountKey = selectedAccount?.value ?? selectedAccount?.additional?.accountKey;

    const { ref, inView } = useInView();

    const { data, error, fetchNextPage, hasNextPage, isLoading, isPending, isFetching, isFetchingNextPage, status, refetch } = useInfiniteQuery({
        queryKey: ["claims", limit, decouncedSearchValue, claimStatus, memberKey, searchBy?.value, accountKey ?? null],
        queryFn: async ({ pageParam }) => {
            let params = pageParam || {};

            if (memberKey) {
                params.memberKey = memberKey
            }

            if (searchBy?.value === "accountKey" && accountKey != null) {
                const key = Number(accountKey);
                if (!Number.isFinite(key)) {
                    return { count: 0, rows: [] };
                }
                return await fetchClaimsByAccountKey({
                    accountKey: key,
                    limit,
                    offset: params.offset || 0,
                });
            }

            if (decouncedSearchValue && searchBy?.value) {
                params[searchBy?.value] = decouncedSearchValue;
            }

            if (claimStatus) {
                params.claimStatus = claimStatus.value
            }

            return await fetchClaimFact({ ...params, limit });
        },

        select: (data) => {
            // console.log({ data });
            return { count: data.pages?.[0]?.count, rows: data.pages.map((page) => page.rows).flatMap((x) => x) };
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => {
            const hasMore = lastPage?.rows?.length === limit;

            if (!hasMore) return undefined;
            return {
                offset: pages.reduce((accum, page) => {
                    return accum + page.rows.length;
                }, 0),
            };
        },
        staleTime: 0,
        enabled: true
    });

    useEffect(() => {
        if (inView) {
            fetchNextPage();
        }
    }, [inView]);

    const debouncedChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchValue(event.target.value?.trim());
        }, 500),
        []
    );

    const columns = ['claimHccId', 'externalClaimNumber', 'claimStatus', 'claimTypeName', 'claimFilingCode', 'memberKey', 'facilityLocationName', 'submittedPayeeTypeCode', 'submittedSubscriberId', 'entryTime', 'receiptDateKey', 'isVoided'];

    const onDetailHandler = async (body) => {

        if (!body?.claimHccId) return {}
        const [claimDetails, claimDetailsByHccID] = await Promise.all([
            fetchClaimDetails({
                ClaimLookupRequest: { claimHccId: body?.claimHccId }
            }),
            fetchClaimDetailsByHccID({
                claimHccNumber: body.claimHccId,
                claimDetailFlags,
            }),
        ])

        return {
            ...claimDetails,
            claimDetailsByHccID,
        }
    }

    return (
        <div className={'container '}
            style={memberKey ? { maxHeight: '100%', overflow: 'scroll' } : {}}
        >
            <p className='title'>Claims ( {data?.count || 0} )</p>
            <p className="sub-title">
                Claims details from Health rules.{" "}
                <Link to="/claims/guide">How HRP adjudicates a claim</Link>
            </p>


            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1rem 1rem 0rem', }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', width: '100%', flexWrap: 'wrap', }}>

                    <Select
                        name={'claimStatus'}
                        className="react-select-container large-input"
                        classNamePrefix="react-select"
                        placeholder={'Claim status'}
                        // inputRef={field.ref}
                        options={claimStatusOptions}
                        value={claimStatus} // Pass the state variable here
                        onChange={(option) => { setClaimStatus(option) }}
                    // value={options.find((c) => c.value === field.value)}
                    // onChange={(val) => field.onChange(val.value)}
                    />

                    {!memberKey && <Select
                        name={'searchBy'}
                        className="react-select-container large-input"
                        classNamePrefix="react-select"
                        placeholder={'Search by'}
                        // inputRef={field.ref}
                        options={searchOptions}
                        value={searchBy} // Pass the state variable here
                        onChange={(option) => {
                            setSearchBy(option);
                            setSearchValue("");
                            setDecouncedSearchValue("");
                            setSelectedAccount(null);
                        }}
                    // value={options.find((c) => c.value === field.value)}
                    // onChange={(val) => field.onChange(val.value)}
                    />}


                    {searchBy?.value === "accountKey" && !memberKey && (
                        <AsyncPaginate
                            placeholder="Search account name, HCC ID, or key"
                            className="react-select-container large-input"
                            classNamePrefix="react-select"
                            debounceTimeout={300}
                            value={selectedAccount}
                            loadOptions={loadAccounts}
                            onChange={setSelectedAccount}
                            styles={customStyles}
                            isClearable
                            isSearchable
                        />
                    )}

                    {searchBy && searchBy.value !== "accountKey" && !memberKey && <input
                        style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                        type="text"
                        id={"search"}
                        placeholder="search..."
                        // list="items"
                        autoComplete="off"
                        // onFocus={() => setCurrentPage(0)}
                        onChange={(e) => {
                            setSearchValue(e.target.value);
                            debouncedChangeHandler(e);
                        }}
                        value={searchValue}
                    />}

                </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem", }}>
                <p
                    style={{ cursor: "pointer", color: "var(--red)" }}
                    onClick={() => {
                        setSearchBy(null)

                        setClaimStatus(null);
                        setDecouncedSearchValue("");
                        setSearchValue("");
                        setSelectedAccount(null);
                    }}
                >
                    Clear filters
                </p>

            </div>


            {status === "loading" ? (
                <p>Loading...</p>
            ) : status === "error" ? (
                <span className='danger'>Error: {error.message}</span>
            ) : (
                <>
                    <SharedTable
                        type={"claims"}
                        columns={columns}
                        items={data?.rows}
                        activeDraftId={null}
                        hasNextPage={hasNextPage}
                        isFetchingNextPage={isFetchingNextPage}
                        fetchNextPage={fetchNextPage}
                        isFetching={isFetching}
                        isLoading={isLoading}
                        // onEditHandler={onEditHandler}
                        onDetailHandler={onDetailHandler}
                        ref={ref}
                    />
                </>
            )}

        </div>
    )
}


export default Claims